import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import PasswordGate from "./components/PasswordGate";
import ScrollToTop from "./components/ScrollToTop";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { trackPageView } from "./utils/analytics";
import { initReferralTracking } from "./utils/referralTracking";
import { REFERRALS_ENABLED } from "./utils/featureFlags";

// Check if we're on production (fundthehope.com)
const isProduction = window.location.hostname === 'fundthehope.com' || window.location.hostname === 'www.fundthehope.com';

// Initialize referral tracking on app load — only when feature is enabled
if (REFERRALS_ENABLED) {
  initReferralTracking();
}

// Google Analytics page tracking component
const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);
  
  return null;
};

// Custom redirect component that preserves query params for login/register
const LoginRedirect = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect');
  const pending = searchParams.get('pending');
  
  // Build the new URL with preserved params
  let newUrl = '/auth?tab=signin';
  if (redirect) {
    newUrl += `&redirect=${encodeURIComponent(redirect)}`;
  }
  if (pending) {
    newUrl += `&pending=${encodeURIComponent(pending)}`;
  }
  
  return <Navigate to={newUrl} replace />;
};

const RegisterRedirect = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect');
  const pending = searchParams.get('pending');
  
  // Build the new URL with preserved params
  let newUrl = '/auth?tab=signup';
  if (redirect) {
    newUrl += `&redirect=${encodeURIComponent(redirect)}`;
  }
  if (pending) {
    newUrl += `&pending=${encodeURIComponent(pending)}`;
  }
  
  return <Navigate to={newUrl} replace />;
};

// Pages
import HomePage from "./pages/HomePage";
import DiscoverPage from "./pages/DiscoverPage";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import CreateCampaignPage from "./pages/CreateCampaignPage";
import DashboardPage from "./pages/DashboardPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import AuthPage from "./pages/AuthPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerificationPage from "./pages/VerificationPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import EditCampaignPage from "./pages/EditCampaignPage";
import PostUpdatePage from "./pages/PostUpdatePage";
import AdminDashboard from "./pages/AdminDashboard";
import PlatformAdminDashboard from "./pages/PlatformAdminDashboard";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import DonationSuccessPage from "./pages/DonationSuccessPage";
import QuickDonatePage from "./pages/QuickDonatePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import UserSettingsPage from "./pages/UserSettingsPage";
import ShareStoryPage from "./pages/ShareStoryPage";
import GoogleAuthCallback from "./pages/GoogleAuthCallback";
import FacebookAuthCallback from "./pages/FacebookAuthCallback";
import MessagesPage from "./pages/MessagesPage";
import WalletPage from "./pages/WalletPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import CampaignAnalyticsPage from "./pages/CampaignAnalyticsPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import PricingPage from "./pages/PricingPage";
import TrustPage from "./pages/TrustPage";
import CareersPage from "./pages/CareersPage";
import PartnersPage from "./pages/PartnersPage";
import GuidelinesPage from "./pages/GuidelinesPage";
import ReportPage from "./pages/ReportPage";
import PressPage from "./pages/PressPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import AccessibilityPage from "./pages/AccessibilityPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import SupportUsPage from "./pages/SupportUsPage";
import SupportUsSuccessPage from "./pages/SupportUsSuccessPage";
import SuccessStoriesPage from "./pages/SuccessStoriesPage";
import InvestorPage from "./pages/InvestorPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import TeamPageDetailPage from "./pages/TeamPageDetailPage";
import CreateTeamPage from "./pages/CreateTeamPage";
import EditTeamPage from "./pages/EditTeamPage";
import TeamLeaderboardPage from "./pages/TeamLeaderboardPage";
import AdminUserDetailPage from "./pages/AdminUserDetailPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import TestCasesDashboard from "./pages/TestCasesDashboard";

function App() {
  const appContent = (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <PageViewTracker />
          <CookieConsentBanner />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/discover/:categoryId" element={<DiscoverPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/campaign/:id" element={<CampaignDetailPage />} />
            <Route path="/campaign/:campaignId/fundraise" element={<CreateTeamPage />} />
            <Route path="/team-page/:teamPageId/edit" element={<EditTeamPage />} />
            <Route path="/campaign/:campaignId/leaderboard" element={<TeamLeaderboardPage />} />
            <Route path="/fundraise/:slug" element={<TeamPageDetailPage />} />
            <Route path="/create" element={<CreateCampaignPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/my-campaigns" element={<DashboardPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/success-stories" element={<SuccessStoriesPage />} />
            <Route path="/how-it-works-old" element={<HowItWorksPage />} />
            {/* Google/Facebook auth callbacks - must come before /auth */}
            <Route path="/auth/google/success" element={<GoogleAuthCallback />} />
            <Route path="/auth/facebook/success" element={<FacebookAuthCallback />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/login" element={<LoginRedirect />} />
            <Route path="/register" element={<RegisterRedirect />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/campaign/:id/edit" element={<EditCampaignPage />} />
            <Route path="/campaign/:id/update" element={<PostUpdatePage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
            <Route path="/platform-admin" element={<PlatformAdminDashboard />} />
            <Route path="/platform-admin/users/:userId" element={<AdminUserDetailPage />} />
            <Route path="/super-admin" element={<SuperAdminDashboard />} />
            <Route path="/test-cases" element={<TestCasesDashboard />} />
            <Route path="/donation-success" element={<DonationSuccessPage />} />
            <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
            <Route path="/donate/:campaignId" element={<QuickDonatePage />} />
            <Route path="/settings" element={<UserSettingsPage />} />
            <Route path="/profile/:userId" element={<PublicProfilePage />} />
            <Route path="/share-story" element={<ShareStoryPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/campaign/:campaignId/analytics" element={<CampaignAnalyticsPage />} />
            {/* Footer Pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/trust" element={<TrustPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/support-us" element={<SupportUsPage />} />
            <Route path="/support-us/success" element={<SupportUsSuccessPage />} />
            <Route path="/guidelines" element={<GuidelinesPage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/investors" element={<InvestorPage />} />
            {/* Redirects */}
            <Route path="/charity" element={<DiscoverPage />} />
            <Route path="/campaigns" element={<DiscoverPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );

  return (
    <ThemeProvider>
    <LanguageProvider>
      {isProduction ? (
        <PasswordGate>{appContent}</PasswordGate>
      ) : (
        appContent
      )}
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
